/**
 * «یه قدم مونده» — ساختِ نمایه بعد از ورود. قرینه‌ی گامِ دومِ
 * apps/mobile/app/auth.tsx.
 *
 * ---------- شکافی که این پر می‌کنه ----------
 * ورود روی وب حساب می‌ساخت ولی **نمایه نه** — نه با ایمیل، نه با گوگل. یعنی
 * هر کسی که فقط از سایت وارد می‌شد، توی این حالت گیر می‌کرد:
 *   • کارتِ هویتِ تنظیمات خالی بود (نه اسم، نه شناسه)
 *   • نامِ کاربری نداشت، پس هیچ دوستی نمی‌تونست پیداش کنه
 *   • و از مایگریشن 0073، کلیدِ ایمیلش هم ثبت نمی‌شد — چون اون تریگر روی
 *     **ساختِ نمایه** می‌نشینه. یعنی برای تطبیقِ دفترچه‌ی بقیه نامرئی بود.
 * روی دیتابیسِ واقعی دو تا حسابِ گوگل دقیقاً همین‌طور مونده بودن.
 *
 * ---------- چرا این‌جا و نه یه مسیرِ جدا ----------
 * گاردِ `Private` این رو **به‌جای** هر صفحه‌ی پشتِ ورود نشون می‌ده. مسیرِ
 * جدا یعنی کاربر باید یه‌جوری کشفش کنه، در حالی که تا نمایه ساخته نشده هیچ
 * صفحه‌ی دیگه‌ای چیزی برای نشون دادن نداره. همون کاری که اپ می‌کنه.
 *
 * ---------- شهر: از «نپرس» به «اجباری» ----------
 * تا دیروز این فرم شهر نمی‌پرسید و به حدسِ IP (`resolveHomeCity`) تکیه
 * می‌کرد، به این دلیل که فرمِ کوتاه‌تر یعنی ورودِ سریع‌تر. آن حدس اما فقط یک
 * حدس است و هیچ‌وقت روی نمایه ننشست: حسابی که از وب ساخته می‌شد `city_id`
 * نداشت، پس فهرستِ کشفش از یک شهرِ دلبخواه شروع می‌کرد تا خودش از تنظیمات
 * دستش بزند — که کسی نمی‌زند، چون نمی‌داند چیزی سرِ جایش نیست.
 *
 * حالا مثل اپ پرسیده می‌شود و اجباری است، با یک راهِ میان‌بر برای کسی که
 * حوصله‌ی گشتن ندارد: دکمه‌ی «از موقعیتم پیدا کن» (FindMyCityRow).
 *
 * ---------- نامِ کاربری: از «اختیاری» به «اجباری» ----------
 * همان الگو، یک قدم جلوتر. این فیلد اختیاری بود و خالی ماندنش یعنی
 * `createMine` خودش یکی می‌ساخت؛ برای اسمِ غیرلاتین همیشه چیزی مثلِ
 * `user48213`، بی‌آنکه کاربر ببیندش، روی فیلدی که هرگز عوض نمی‌شود. حالا
 * اجباری است ولی خالی تحویل داده نمی‌شود: پیشنهاد از روی اسم (یا ایمیل)
 * پر می‌شود، آزاد بودنش همین‌جا پرسیده می‌شود، و «گرفته شده» به‌جای خطای
 * بعد از زدنِ دکمه، یک خطِ زیرِ خودِ فیلد است. منطقش در
 * `core/lib/useUsernameField` است چون فرمِ اپ هم مو به مو همین را می‌خواهد.
 *
 * ---------- چرا تیکِ شرایط این‌جاست و نه توی فرمِ ورود ----------
 * چون «ادامه با گوگل» دکمه‌ی ورود **و** ثبت‌نام است: کسی که حساب ندارد،
 * همان‌جا برایش ساخته می‌شود. تیک قبلاً فقط در حالتِ «ساخت حساب» شرط بود،
 * پس کافی بود روی زبانه‌ی «ورود» بمانی و با گوگل داخل شوی — بی هیچ تیکی.
 * این‌جا آن سوراخ بسته است، چون این تنها قدمی است که هر حسابی، با هر
 * ارائه‌دهنده‌ای، از آن رد می‌شود؛ و ثبتِ پذیرش هم دقیقاً همین‌جا اتفاق
 * می‌افتد (`profileRepo.createMine`).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { profileRepo } from '@/core/data/socialRepo';
import { useSession } from '@/core/session';
import { useUsernameField, type UsernameField } from '@/core/lib/useUsernameField';
import { t } from '@/core/lib/i18n';
import { markTourPending } from '@/core/lib/tour';
import { reportError } from '@/core/lib/report';
import { defaultDisplayName, providerProfile } from '@/core/lib/providerProfile';
import { handleFilterBlocked } from '@/ui/filterBlocked';
import { Card, Checkbox, Help } from '@/ui/kit';
import { WhereYouLive, useWhereYouLive } from '@/features/profile/WhereYouLive';
import { FindMyCityRow } from '@/features/profile/FindMyCityRow';

/**
 * پیامِ خطای دیتابیس → یه جمله‌ی قابلِ فهم.
 *
 * «گرفته شده» حالا تقریباً همیشه پیش از رسیدن به این‌جا سرِ خودِ فیلد گفته
 * می‌شود؛ این‌جا برای آن حالتِ باقی‌مانده است که کوئریِ آزاد بودن نمی‌تواند
 * ببیندش: نامِ کاربریِ حسابی که حذف شده — سیاستِ `p_sel` آن ردیف را پنهان
 * می‌کند ولی قیدِ یکتاییِ ستون هنوز سرِ جایش است.
 */
const humanError = (msg: string): string =>
  (/duplicate key|unique/i.test(msg) ? t('usernameTaken') : t('errorRetry'));

/** خطِ زیرِ فیلدِ نامِ کاربری. «خالی» و «نامعلوم» عمداً چیزی نمی‌گویند. */
function UsernameNote({ field }: { field: UsernameField }) {
  switch (field.state) {
    case 'checking': return <p className="field-note">{t('usernameChecking')}</p>;
    case 'short': return <p className="field-note bad">{t('usernameTooShort')}</p>;
    case 'taken': return <p className="field-note bad">{t('usernameTaken')}</p>;
    case 'free': return <p className="field-note ok">{t('usernameFree', { u: field.value })}</p>;
    default: return null;
  }
}

export function CreateProfilePage() {
  const { session, refreshProfile, signOut } = useSession();
  /**
   * آن‌چه سرویسِ ورود داده دوباره پرسیده نمی‌شود (WHOSUP-307، قرینه‌ی
   * auth.tsx): اسم آمده → فیلدش از قبل پر و قابلِ تغییر؛ نیامده → فیلد اختیاری و خالی‌اش
   * یعنی اسمِ پیش‌فرض، که در تنظیمات عوض می‌شود. عکسِ گوگل هم می‌نشیند.
   */
  const fromProvider = providerProfile(session?.user?.user_metadata);
  const [name, setName] = useState(fromProvider.name);
  /**
   * نامِ کاربری: اجباری، ولی خالی تحویلِ کاربر داده نمی‌شود.
   *
   * تا دیروز اختیاری بود و اگر خالی می‌ماند `createMine` خودش یکی می‌ساخت —
   * که برای اسمِ فارسی همیشه چیزی مثلِ `user48213` از آب درمی‌آمد، بی‌آنکه
   * کاربر ببیندش، روی فیلدی که بعداً عوض نمی‌شود. حالا پیشنهاد همین‌جا
   * ساخته و نشان داده می‌شود تا تاییدش کند، و آزاد بودنش هم قبل از ساختِ
   * حساب معلوم است نه بعدش. کلِ رفتارش در `core/lib/useUsernameField`.
   */
  const uname = useUsernameField(name, session?.user?.email);
  // بازخوردِ تستِ دستی (WHOSUP-307): پیشنهادِ نامِ کاربری از همان اول جلویش
  // است، نه فقط بعد از زدنِ «ساختن» با فیلدِ خالی. قرینه‌ی auth.tsx.
  useEffect(() => {
    if (!name && fromProvider.name) setName(fromProvider.name);
    if (!uname.value && !uname.suggestion) void uname.suggest();
  }, [fromProvider.name]);   // eslint-disable-line react-hooks/exhaustive-deps
  // کشور و بعد شهر، هر دو از فهرست — همون مؤلفه‌ی اپ.
  const place = useWhereYouLive();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const p = await profileRepo.createMine(uname.value,
        name.trim() || defaultDisplayName(session?.user?.user_metadata, session?.user?.email, uname.value), {
          acceptedTerms,
          city: place.city || undefined,
          country: place.country?.name || undefined,
          // شناسه کنارِ اسم می‌شینه، نه به‌جاش — نگاه کن به Profile.cityId.
          cityId: place.cityId,
        });
      if (fromProvider.avatarUrl) await profileRepo.importAvatarFromUrl(p.id, fromProvider.avatarUrl);
      return p;
    },
    onSuccess: () => {
      /**
       * تورِ راهنما (WHOSUP-83) — علامتش دقیقاً همین‌جاست، سرِ ساختِ نمایه،
       * نه سرِ هر ورود: «نمایه نداشت و حالا دارد» تنها لحظه‌ای است که یعنی
       * این آدم واقعاً تازه است. بدونِ این شرط، هر ورود از یک مرورگرِ تازه
       * هم یک تورِ ناخواسته می‌شد. کارتش را `Shell` نشان می‌دهد.
       */
      const uid = session?.user?.id;
      if (uid) void markTourPending(uid);
      void refreshProfile();
    },
    onError: (e) => {
      if (handleFilterBlocked(e)) return;
      reportError('web.profile.create', e);
      setError(humanError(e instanceof Error ? e.message : String(e)));
    },
  });

  /**
   * دکمه فقط پشتِ تیکِ شرایط خاموش است، نه پشتِ فیلدهای خالی.
   *
   * قبلاً هر فیلدِ خالی دکمه را خاموش می‌کرد. مشکلش این بود که دکمه‌ی
   * خاموش **دلیل نمی‌گوید**: کاربر می‌ماند که چه چیزی کم است، و روی
   * فیلدی مثل نامِ کاربری که اجباری بودنش تازه است، حدس زدن سخت‌تر هم
   * می‌شود. حالا فشار می‌دهی و همان‌جا می‌گوید چه کم است — و برای نامِ
   * کاربری یک پیشنهاد هم جلویت می‌گذارد.
   *
   * تیک استثناست و می‌ماند: خودِ تیک **عملِ پذیرش** است، نه توصیفِ یک
   * فیلد. نمی‌شود با پیام کسی را به پذیرفتن رساند.
   */
  const submit = () => {
    setError(null);
    if (!uname.value) {
      setError(t('usernameRequired'));
      // و همین‌جا یکی جلویش می‌گذاریم که با یک کلیک بنشیند. عمداً از قبل
      // توی فیلد ننشسته: نامِ کاربری تا ابد روی حساب می‌ماند و فیلدِ
      // از-قبل-پر خوانده نمی‌شود.
      void uname.suggest();
      return;
    }
    if (!uname.ready) { setError(t('usernameTaken')); return; }
    // شهر اجباریه — دلیلش بالای همین فایل. قرینه‌ی auth.tsx در اپ.
    if (!place.cityId) { setError(t('cityRequired')); return; }
    if (!acceptedTerms) { setError(t('termsMustAccept')); return; }
    create.mutate();
  };

  return (
    <div className="feed">
      <h1 className="feed-title">{t('oneStepLeft')}</h1>

      <Card>
        {/* با کدوم حساب واردی — به‌ویژه بعد از ورود با گوگل، تنها جایی که
            جوابِ «کدوم ایمیل؟» رو می‌ده. */}
        <h3>{t('accountEmail')}</h3>
        <p className="mono-line">{session?.user?.email ?? ''}</p>
      </Card>

      <Card>
        <input className="input" placeholder={t('displayName')} value={name}
          onChange={(e) => setName(e.target.value)} />

        {/* توضیحِ نامِ کاربری پشتِ یک «؟» کنارِ خودِ فیلد است، نه پاراگرافی
            پایینِ کارت: آن‌جا کسی که وسطِ پر کردنِ فیلد سوالش پیش می‌آید
            نمی‌بیندش. همان الگوی فرمِ پیشنهاد (SuggestPage). */}
        <label className="field">
          <span>{t('username')}<Help text={t('usernameNote')} /></span>
          <input className="input" autoCapitalize="none"
            value={uname.value} onChange={(e) => uname.set(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
          {/* خطِ حالِ فیلد — همان‌جا زیرِ خودش، نه پیامی پایینِ فرم که
              کسی وسطِ تایپ نمی‌بیندش. «گرفته شده» هم همین‌جاست: تا دیروز
              فقط بعد از زدنِ «ساختن» و به شکلِ خطای دیتابیس دیده می‌شد. */}
          <UsernameNote field={uname} />
        </label>

        {/* پیشنهاد از اولِ صفحه جلویش است (WHOSUP-307) ولی توی فیلد
            نمی‌نشیند؛ یک کلیک می‌نشاندش. */}
        {uname.suggestion ? (
          <button type="button" className="btn ghost" onClick={uname.accept}>
            {t('usernameUse', { u: uname.suggestion })}
          </button>
        ) : null}

        {/* میان‌برِ موقعیت و کشور/شهر یک موضوع‌اند — یک کادر با یک عنوان
            دورشان همین را می‌گوید؛ بدونِ کادر، دکمه‌ی موقعیت وسطِ فرم معلق
            بود و ربطش به دو فیلدِ زیرش دیده نمی‌شد. */}
        <div className="place-box">
          <p className="place-box-title">{t('whereYouLiveTitle')}</p>
          <FindMyCityRow state={place} />
          <WhereYouLive state={place} />
        </div>

        {/* شرطِ ساختِ حساب، نه یه یادداشتِ کنارِ فرم — بند ۱ سند. */}
        <div>
          <Checkbox label={t('termsAccept')} checked={acceptedTerms}
            onChange={setAcceptedTerms} />
          <Link to="/legal" className="dim">{t('termsRead')}</Link>
        </div>

        {error ? <p className="error">{error}</p> : null}

        <button type="button" className="btn fill block"
          disabled={create.isPending || !acceptedTerms}
          onClick={submit}>
          {create.isPending ? '…' : t('create')}
        </button>

        {/*
          راهِ برگشت.

          بدونِ این، کسی که «ادامه با گوگل» را زده و همین‌جا پشیمان شده در
          بن‌بست بود: نشست برقرار است، پس `Private` او را به هر صفحه‌ای که
          برود به همین فرم برمی‌گرداند، و دکمه‌ی خروجی هم در کار نیست چون
          تنظیمات خودش پشتِ همین فرم است. پشیمان شدن باید همیشه ممکن باشد،
          به‌ویژه سرِ صفحه‌ای که دارد شرایط را می‌پذیراند.

          خروج کافی است و ناوبریِ دستی لازم نیست: با رفتنِ نشست، `Private`
          خودش به /auth می‌فرستد.
        */}
        <button type="button" className="btn ghost block"
          disabled={create.isPending} onClick={() => void signOut()}>
          {t('authBackToSignIn')}
        </button>
      </Card>
    </div>
  );
}
